import {
  HotelBookingSummary,
  type SummaryFormValues,
} from "./HotelBookingSummary";
import { type HotelBooking } from "@/app/lib/types/types";
import type {
  FindHotelsOutputData,
  FindUserTravelersTravelers,
} from "@/app/lib/types/codegen";
import BottomDrawer from "../flight-purchase/BottomDrawer";
import { usePaymentContext } from "@/app/lib/context/PaymentContext";
import { formatCurrency } from "@/app/lib/utils/currency";
import {
  includeBnplFee,
  calculateMonthlyInstallment,
} from "@/app/lib/utils/bnpl-fee";
import { getTravellerTypeAtTravelDate } from "@/app/lib/utils/ageValidation";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { array, boolean, object, string } from "zod";
export interface HotelBookingSummaryFormProps {
  booking: HotelBooking;
  travelDate: Date;
  hotel: FindHotelsOutputData;
  loading: boolean;
  disabledSubmit?: boolean;
  rooms?: { adults: number; children: number }[];
  buttonLabel: string;
  selectedTravellers: FindUserTravelersTravelers[];
  handleSubmit: (values: SummaryFormValues) => void;
}

export const HotelBookingSummaryForm = ({
  booking,
  travelDate,
  hotel,
  loading,
  rooms = [],
  buttonLabel,
  disabledSubmit,
  selectedTravellers,
  handleSubmit: onSubmit,
}: HotelBookingSummaryFormProps) => {
  const { paymentType, selectedInstallments } = usePaymentContext();

  const hotelTotalPrice =
    booking?.rooms.reduce((acc, room) => acc + (room.price?.value || 0), 0) ||
    0;

  // Calculate BNPL fee if BNPL is selected
  const includedBnplFee = includeBnplFee(hotelTotalPrice);
  const totalPrice = paymentType === "bnpl" ? includedBnplFee : hotelTotalPrice;

  const validationSchema = object({
    termsAndConditions: boolean().refine((v) => v === true),
    leadTraveler: string().min(1),
    roomAssigments: object(
      Object.fromEntries(
        booking.rooms.map((room, index) => [
          room.roomToken,
          object({
            travelers: array(string()).length(
              rooms[index].adults + rooms[index].children,
            ),
          }),
        ]),
      ),
    ).required(),
  });

  const leadTraveler = selectedTravellers.find(
    (traveller) =>
      getTravellerTypeAtTravelDate(traveller.birthDate, travelDate) === "adult",
  );

  const methods = useForm<SummaryFormValues>({
    defaultValues: {
      roomAssigments:
        booking.rooms.length === 1
          ? {
              [booking.rooms[0].roomToken]: {
                travelers: selectedTravellers.map((traveller) => traveller.id),
              },
            }
          : {},
      leadTraveler: leadTraveler?.id || "",
      termsAndConditions: false,
    },
    resolver: zodResolver(validationSchema),
  });

  const {
    handleSubmit,
    formState: { isValid, isDirty },
  } = methods;

  return (
    <FormProvider {...methods}>
      <form>
        <HotelBookingSummary
          hotel={hotel}
          hotelBookingData={booking}
          selectedTravellers={selectedTravellers}
          rooms={rooms}
          travelDate={travelDate}
        />
        <BottomDrawer
          loading={loading}
          totalPrice={
            paymentType === "bnpl" ? undefined : formatCurrency(totalPrice)
          }
          payLaterPrice={
            paymentType === "bnpl"
              ? formatCurrency(
                  calculateMonthlyInstallment(
                    includedBnplFee,
                    selectedInstallments,
                  ),
                )
              : undefined
          }
          buttonLabel={buttonLabel}
          onButtonClick={() => handleSubmit((data) => onSubmit(data))()}
          disabled={disabledSubmit || !isValid || !isDirty}
        />
      </form>
    </FormProvider>
  );
};
